import * as React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import { Button } from "@mui/material";
import { TrialEndWarningModalContainer, styles } from "./styles";
import { TrialEndWarningModalProps } from "./types";
import { checkoutStripeCustomerPortal } from "../../services/stripeService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import CloseIcon from "@mui/icons-material/Close";
import { useCallback, useEffect, useState } from "react";
import { getPaymentMethod } from "../TrackComponents/TrackHandler/trackHandlers";
import Mixpanel from "../../utils/mixpanel";

export const TrialEndWarningModal: React.FC<TrialEndWarningModalProps> = ({
  trialEndTime,
  userId,
  stripeCustomerId,
}) => {
  const { getValidAccessToken } = useGetValidAccessToken();
  const [timeToTrialEnd, setTimeToTrialEnd] = useState<number>(
    trialEndTime - Math.floor(Date.now() / 1000)
  );
  const [showModal, setShowModal] = useState(false);

  const checkTrialStatus = useCallback(async () => {
    const currentTimestamp: number = Math.floor(Date.now() / 1000);
    const difference: number = trialEndTime - currentTimestamp;
    setTimeToTrialEnd(difference);
    const token = await getValidAccessToken();
    const paymentMethod = await getPaymentMethod(token, userId);
    if (
      Math.ceil(difference / 86400) <= 7 &&
      !paymentMethod?.getHasPaymentMethod() &&
      trialEndTime !== 0
    ) {
      setShowModal(true);
    }
  }, [getValidAccessToken, trialEndTime, userId]);

  useEffect(() => {
    checkTrialStatus();
  }, [checkTrialStatus]);

  const getCustomerPortalLink = async () => {
    if (!stripeCustomerId) return;
    Mixpanel.track("billing_issue_modal_continue", {
      message: getTrialEndMessage(),
    });
    const accessToken = await getValidAccessToken();
    await checkoutStripeCustomerPortal(stripeCustomerId, accessToken);
  };

  const getTrialEndMessage = () => {
    const trialEndsIn = Math.ceil(Math.abs(timeToTrialEnd) / 86400);
    if (trialEndsIn === 1) return `Your trial is ending in 1 day.`;
    else return `Your trial is ending in ${trialEndsIn} days.`;
  };

  return (
    <>
      <Modal
        open={showModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <TrialEndWarningModalContainer>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setShowModal(false)}>
              <CloseIcon />
            </Button>
          </Box>
          <Box sx={{ padding: "50px" }}>
            {timeToTrialEnd <= 0 ? (
              <Box>
                <Typography
                  sx={{ marginBottom: "40px" }}
                  id="modal-modal-title"
                  variant="h4"
                  component="h2"
                >
                  Your trial has ended.
                  <span style={{ display: "block" }}>
                    Please enter your payment information.
                  </span>
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography
                  sx={{ marginBottom: "40px" }}
                  id="modal-modal-title"
                  variant="h4"
                  component="h2"
                >
                  {getTrialEndMessage()}
                  <span style={{ display: "block" }}>
                    Please enter your payment information.
                  </span>
                </Typography>
              </Box>
            )}
            <Box sx={styles.trialEndWarningModalButtonsContainer}>
              <Button onClick={getCustomerPortalLink} sx={styles.buttons}>
                Continue
              </Button>
            </Box>
          </Box>
        </TrialEndWarningModalContainer>
      </Modal>
    </>
  );
};
